import React, { useEffect, useMemo, useState } from 'react';
import {
  ButtonGroup,
  CardProps,
  Container,
  Grid,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemIcon,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import { Card } from '../common/Card';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';
import { useLLMDiagnose } from '../../contexts/LLMDiagnoseContext';
import { AttachFile, AutoAwesome, Mic, Stop } from '@mui/icons-material';
import { useDiagnosesPageDispatch } from '../../contexts/DiagnosesPageContext';
import { usePatientInfoDispatch } from '../../contexts/CurrentPatientInfoContext';
import { useRWDContext } from '../../contexts/RWDContext';
import Loader from '../Loader';

import API from '../../api/ApiLayer';
import { useVeterinarian } from '../../contexts/VeterinarianContext';
import moment from 'moment';
import { FileUploader } from 'react-drag-drop-files';
import { EULA_UPDATED_AT } from '../EULAModal';

import { LoadingButton } from '@mui/lab';

import { fetchAuthSession } from 'aws-amplify/auth';

import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
  Result,
} from '@aws-sdk/client-transcribe-streaming';
import mic from 'microphone-stream';
import MicrophoneStream from 'microphone-stream';
import { useSoapNotes } from '../../contexts/SoapNotesContext';
import { useTreatmentsPageDispatch } from '../../contexts/TreatmentsPageContext';
import { useDosagesPageDispatch } from '../../contexts/DosagesPageContext';
import { gekkoApi, CaseData } from '@gekkovet-oy/shared-components';

const useStyles = makeStyles()((theme: Theme) => ({
  continueButton: {
    marginTop: theme.spacing(2),
  },
  infoTextAlignment: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  containerText: {},
  fileUploader: {
    border: `dashed 2px ${theme.palette.primary.main} !important`,
    display: 'flex',
    padding: theme.spacing(1),
    textAlign: 'center',
  },
  transcribeBox: {
    border: 'none', // Remove border since it's inside the textarea
    borderRadius: '0 0 3px 3px', // Match textarea's bottom border radius
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    minHeight: '56px',
    position: 'absolute',
    bottom: '3px', // More space from the bottom border
    left: '2px', // More space from the left border
    right: '2px', // More space from the right border
    zIndex: 1,
    marginTop: theme.spacing(1),
  },
  transcribeControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

export default function LLMDiagnose({ style }: CardProps) {
  const { classes } = useStyles();
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [threadAndRunId, setThreadAndRunId] = useState('');
  const [loading, setLoading] = useState(false);
  const [processTimer, setProcessTimer] = useState<NodeJS.Timeout | null>(null);
  const [files, setFiles] = useState<File[] | null>(null);
  const dispatchPageDiagnoses = useDiagnosesPageDispatch();
  const dispatchPatientInfo = usePatientInfoDispatch();
  const dispatchPageTreatments = useTreatmentsPageDispatch();
  const dispatchPageDrugDosages = useDosagesPageDispatch();
  const { veterinarian } = useVeterinarian();
  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState<string>(() => {
    return localStorage.getItem('gekko/transcribe-language') || 'en-US';
  });
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { setSoapNotes, setSoapNotesGeneratedAt } = useSoapNotes();

  const allowUpload = useMemo(() => {
    if (!veterinarian) return false;
    return moment(veterinarian.EULAAcceptedAt).isAfter(EULA_UPDATED_AT);
  }, [veterinarian]);

  const [recordingStarting, setRecordingStarting] = useState(false);
  const [activeStream, setActiveStream] = useState<MicrophoneStream | null>(
    null
  );
  const [transcribeLines, setTranscribeLines] = useState<
    { text: string; speaker: string }[]
  >([]);

  const [partialLine, setPartialLine] = useState<{
    text: string;
    speaker: string;
  } | null>(null);

  const speakers = useMemo(() => {
    const speakers: Record<string, string> = {};
    transcribeLines.forEach((line) => {
      if (!speakers[line.speaker]) {
        speakers[line.speaker] = `Speaker ${Object.keys(speakers).length + 1}`;
      }
    });
    if (partialLine && !speakers[partialLine.speaker]) {
      speakers[partialLine.speaker] = `Speaker ${
        Object.keys(speakers).length + 1
      }`;
    }
    return speakers;
  }, [transcribeLines, partialLine]);

  const {
    freeText,
    setFreeText,
    setExtractedSymptoms,
    setExtractedAnimal,
    setIsActiveLLMDiagnoseFlow,
  } = useLLMDiagnose();
  // clear the free text when the component is mounted
  useEffect(() => {
    setFreeText('');
  }, [setFreeText]);
  // clear selected symptoms upon mount
  useEffect(() => {
    setExtractedSymptoms({});
    setExtractedAnimal(null);
  }, []); // eslint-disable-line
  // clear active llm flow
  useEffect(() => {
    setIsActiveLLMDiagnoseFlow(false);
  }, [setIsActiveLLMDiagnoseFlow]);
  // on init, reset all diagnose data
  useEffect(() => {
    dispatchPageDiagnoses({ type: 'resetAll' });
    dispatchPatientInfo({ type: 'resetPatientInfo' });
    dispatchPageTreatments({ type: 'reset' });
    dispatchPageDrugDosages({ type: 'reset' });
  }, []); // eslint-disable-line
  // on init, reset soap notes
  useEffect(() => {
    setSoapNotes('');
    setSoapNotesGeneratedAt(undefined);
  }, []); // eslint-disable-line
  // check if is sm screen
  const theme = useTheme();
  const isSmScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { track: trackRWD } = useRWDContext();
  const rwd = () => {
    trackRWD({ actionName: 'diagnostics_start_page' });
  };

  useEffect(() => {
    if (attachmentDialogOpen) return;
    setFiles(null);
  }, [attachmentDialogOpen]);

  useEffect(() => {
    // stop microphone capture if navigated away
    if (!activeStream) return;
    return () => {
      if (activeStream) {
        try {
          activeStream.stop();
          activeStream.destroy();
        } catch (e) {
          // omit error handling
        }
      }
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!threadAndRunId || processTimer)
      return () => {
        if (processTimer) {
          clearInterval(processTimer);
        }
      };
    setProcessTimer(
      setInterval(async () => {
        try {
          const [threadId, runId] = threadAndRunId.split('/');
          const { body } = await API.getRequest<{
            status: string;
            messages: string[];
          }>(`/assistants/result?thread_id=${threadId}&run_id=${runId}`, {});
          if (body!.status !== 'in_progress') {
            clearInterval(processTimer!);
            setProcessTimer(null);
            setFileProcessing(false);
            setThreadAndRunId('');
            if (body!.status === 'completed') {
              setFreeText(
                (freeText ? freeText + '\n\n' : '') + body!.messages[0]
              );
            } else {
              setFreeText(
                (freeText ? freeText + '\n\n' : '') +
                  'Error processing attachment'
              );
            }
          }
        } catch (e) {
          console.error('Error checking attachment status', e);
        }
      }, 2000)
    );
    return () => {
      if (processTimer) {
        clearInterval(processTimer);
      }
    };
  }, [threadAndRunId, processTimer, setFreeText, freeText]);

  const uploadAttachment = async (file: File) => {
    const resp = await API.postRequest<{ url: string; filename: string }>(
      '/assistants/attachment',
      { type: 'application/pdf', name: file!.name }
    );
    if (!resp.body) {
      throw new Error('No response from the server');
    }
    const { url, filename } = resp.body; // S3 url
    const uploadResp = await fetch(url, {
      method: 'PUT',
      body: file!,
    });
    if (!uploadResp.ok) {
      throw new Error('Error uploading attachment');
    }
    return filename;
  };

  const uploadAttachments = async () => {
    setFileProcessing(true);
    setAttachmentDialogOpen(false);
    const filenames = await Promise.all(
      Array.from(files!).map(uploadAttachment)
    );
    try {
      const { body } = await API.postRequest<{
        thread_id: string;
        run_id: string;
      }>('/assistants/request', {
        filenames,
      });
      setThreadAndRunId(`${body!.thread_id}/${body!.run_id}`);
    } catch (e) {
      console.error('Error uploading attachment', e);
      setFileProcessing(false);
      setFreeText(
        (freeText ? freeText + '\n\n' : '') + 'Error processing attachment'
      );
    }
  };

  const startRecording = async () => {
    setRecordingStarting(true);

    const session = await fetchAuthSession();
    const credentials = session.credentials;

    const client = new TranscribeStreamingClient({
      credentials,
      region: 'eu-west-1',
    });

    const micStream = new mic();
    setActiveStream(micStream);
    micStream.setStream(
      await window.navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      })
    );

    const audioGenerator = async function* () {
      for await (const chunk of micStream) {
        yield {
          AudioEvent: {
            AudioChunk: convertAudioChunk(chunk),
          },
        };
      }
    };

    const command = new StartStreamTranscriptionCommand({
      LanguageCode: selectedLanguage as any, // TypeScript definitions are missing language codes
      MediaSampleRateHertz: 44100,
      MediaEncoding: 'pcm',
      AudioStream: audioGenerator(),
      ShowSpeakerLabel: true,
    });

    const response = await client.send(command);
    console.log('got response', response);

    setRecordingStarting(false);

    // This snippet should be put into an async function
    if (!response.TranscriptResultStream) {
      console.log('no result stream');
      return;
    }
    for await (const event of response.TranscriptResultStream) {
      //console.log('got event', event);
      if (event.TranscriptEvent) {
        // Get multiple possible results
        const results = event.TranscriptEvent.Transcript?.Results;
        // Print all the possible transcripts
        if (results?.length) {
          const result = results[0] as Result;
          if (!result.Alternatives?.length) {
            continue;
          }
          const speaker = result.Alternatives[0].Items?.[0]?.Speaker || '0';

          if (result.IsPartial) {
            setPartialLine({
              speaker,
              text: result.Alternatives[0].Transcript || '',
            });
          } else {
            setTranscribeLines((prev) => [
              ...prev,
              {
                speaker,
                text: result.Alternatives![0].Transcript || '',
              },
            ]);
            setPartialLine(null);
          }
        }
      }
    }
  };

  const handleFiles = (newFiles: FileList) => {
    const list = Array.from(files || []);
    Array.from(newFiles).forEach((file) => {
      list.push(file);
    });
    setFiles(list);
    setAttachmentDialogOpen(true);
  };

  const freeTextWithTranscribeLines = () => {
    let res = '';
    if (freeText) {
      res += freeText;
      if (transcribeLines.length || partialLine) {
        res += '\n';
      }
    }

    if (transcribeLines.length) {
      res += transcribeLines
        .map((line) => `${speakers[line.speaker]}: ${line.text}`)
        .join('\n');

      if (partialLine) {
        res += '\n';
      }
    }

    if (partialLine) {
      res += `${speakers[partialLine.speaker]}: ${partialLine.text}`;
    }

    return res;
  };

  const handleContinue = () => {
    setFreeText(freeTextWithTranscribeLines());
    rwd();
    setLoading(true);
    gekkoApi
      .post<CaseData>('/cases', {
        body: {
          notes: freeTextWithTranscribeLines(),
        },
      })
      .then((response) => {
        navigate(`/cases/${response.id}`);
      });
  };

  // Language selection handlers
  const handleLanguageDialogOpen = () => {
    setLanguageDialogOpen(true);
    setSearchQuery('');
  };

  const handleLanguageDialogClose = () => {
    setLanguageDialogOpen(false);
  };

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);
    localStorage.setItem('gekko/transcribe-language', languageCode);
    setLanguageDialogOpen(false);
  };

  // Filter languages based on search query
  const filteredLanguages = useMemo(() => {
    if (!searchQuery) return awsTranscribeLanguages;

    const query = searchQuery.toLowerCase();
    return awsTranscribeLanguages.filter(
      (language) =>
        language.name.toLowerCase().includes(query) ||
        language.code.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Find the selected language name
  const selectedLanguageName = useMemo(() => {
    const language = awsTranscribeLanguages.find(
      (lang) => lang.code === selectedLanguage
    );
    return language ? language.name : selectedLanguage;
  }, [selectedLanguage]);

  return (
    <Card
      title="Diagnostics"
      pageView="noPadding"
      withoutMarginBottom
      headerCentered
      style={style}
    >
      <Grid container style={{ height: '100%' }}>
        <Grid item className={classes.infoTextAlignment} xs={12}>
          <Container
            fixed
            className={classes.containerText}
            style={{ position: 'relative' }}
          >
            <Typography align="center">
              <AutoAwesome /> Describe the patient and your findings to start
              diagnosing
            </Typography>

            {/*allowUpload && (
              <Button
                variant="contained"
                color="primary"
                sx={{ position: 'absolute', top: 0, right: 0 }}
                onClick={() => setAttachmentDialogOpen(true)}
              >
                <AttachFile />
                Attach File
              </Button>
            )*/}
          </Container>
        </Grid>
        <Grid
          xs={12}
          lg={12}
          sm={12}
          md={12}
          item
          flexGrow={'1'}
          style={{ position: 'relative' }}
        >
          <TextField
            multiline
            disabled={!!activeStream}
            rows={isSmScreen ? 10 : 15}
            value={freeTextWithTranscribeLines()}
            sx={{
              height: '100%',
              '& .MuiInputBase-root': {
                paddingBottom: '72px', // Make room for the transcribe bar + margin
              },
            }}
            onChange={(ev) => setFreeText(ev.target.value)}
            placeholder="For example: Bella, 6yo intact female golden retriever, has started vomiting and has diarrhea."
            fullWidth
          />

          {/* Transcribe Bar - Fixed to bottom of textarea */}
          <div className={classes.transcribeBox}>
            <div className={classes.transcribeControls}>
              <Typography
                variant="body2"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Mic fontSize="small" /> Voice Transcription
              </Typography>
            </div>

            <div className={classes.transcribeControls}>
              {!activeStream && !recordingStarting && (
                <React.Fragment>
                  <LoadingButton
                    loading={false}
                    onClick={startRecording}
                    color="primary"
                    variant="contained"
                    size="small"
                  >
                    <Mic />
                    Record
                  </LoadingButton>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleLanguageDialogOpen}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {selectedLanguageName}
                    <ArrowDropDownIcon />
                  </Button>
                </React.Fragment>
              )}

              {(activeStream || recordingStarting) && (
                <LoadingButton
                  loading={recordingStarting}
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={async () => {
                    activeStream?.stop();
                    activeStream?.destroy();
                    setFreeText(freeTextWithTranscribeLines());
                    setTranscribeLines([]);
                    setPartialLine(null);
                    setActiveStream(null);
                  }}
                >
                  <Stop />
                  Stop
                </LoadingButton>
              )}

              {!allowUpload && !activeStream && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ fontSize: '0.7rem' }}
                >
                  EULA required
                </Typography>
              )}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} lg={12} sm={12} md={12}>
          {/* File Upload and Continue Button Row */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={9} style={{ paddingTop: '1em' }}>
              {allowUpload && !activeStream && (
                <FileUploader
                  classes={[classes.fileUploader]}
                  multiple
                  handleChange={handleFiles}
                  name="file"
                  types={['PDF']}
                >
                  <AttachFile />
                  Attach files by dragging and dropping here or by clicking this
                  area
                </FileUploader>
              )}
            </Grid>

            {/* Continue Button */}
            <Grid
              item
              xs={12}
              md={3}
              textAlign="right"
              style={{ paddingTop: '1em' }}
            >
              <ButtonGroup className={classes.continueButton}>
                <Button
                  title="Continue"
                  onClick={handleContinue}
                  disabled={!!activeStream}
                  color="primary"
                  variant="contained"
                >
                  Continue
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Language Selection Modal Dialog */}
      <Dialog
        open={languageDialogOpen}
        onClose={handleLanguageDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Language</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            placeholder="Search languages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 2, maxHeight: '400px', overflow: 'auto' }}>
            <List>
              {filteredLanguages.map((language, index) => (
                <React.Fragment key={language.code}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    button
                    onClick={() => handleLanguageSelect(language.code)}
                    selected={language.code === selectedLanguage}
                  >
                    {language.code === selectedLanguage && (
                      <ListItemIcon>
                        <CheckIcon color="primary" />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={language.name}
                      secondary={language.code}
                      inset={language.code !== selectedLanguage}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLanguageDialogClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
      >
        <DialogTitle>Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            You can attach files like lab results to help with the diagnosis.
          </Typography>
          <Typography>
            The files will be processed and appended to the rest of your text.
          </Typography>
          {/*<FormControl>
            {!file && (
              <Button
                variant="contained"
                color="primary"
                component="label"
                style={{ marginTop: '1em' }}
              >
                Attach File
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={(ev) => setFile(ev.target.files![0])}
                />
              </Button>
            )}
            {file && (
              <Box sx={{ marginTop: 1, marginBottom: 1 }}>
                <Typography>File: {file?.name}</Typography>
              </Box>
            )}
          </FormControl>*/}
          <Box sx={{ marginTop: 1, marginBottom: 1 }}>
            {files && (
              <ul>
                {Array.from(files).map((file) => (
                  <li key={file.name}>{file.name}</li>
                ))}
              </ul>
            )}
          </Box>
          <FileUploader
            classes={[classes.fileUploader]}
            handleChange={handleFiles}
            multiple
            types={['PDF']}
          >
            <AttachFile />
            Attach more files by dragging and dropping here or by clicking this
            area
          </FileUploader>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!files}
            onClick={uploadAttachments}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
      <Loader showLoader={loading || fileProcessing} />
    </Card>
  );
}

// Convert audio chunk to buffer
function convertAudioChunk(chunk: Buffer) {
  const rawAudio = mic.toRaw(chunk);

  let offset = 0;
  const buffer = new ArrayBuffer(rawAudio.length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < rawAudio.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, rawAudio[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return Buffer.from(buffer) as any;
  //return new Uint16Array(buffer);
  /*const raw = mic.toRaw(chunk);
  return new Uint8Array(raw.buffer);*/
}
const awsTranscribeLanguages = [
  { code: 'ab-GE', name: 'Abkhaz' },
  { code: 'af-ZA', name: 'Afrikaans' },
  { code: 'ar-AE', name: 'Arabic, Gulf' },
  { code: 'ar-SA', name: 'Arabic, Modern Standard' },
  { code: 'hy-AM', name: 'Armenian' },
  { code: 'ast-ES', name: 'Asturian' },
  { code: 'az-AZ', name: 'Azerbaijani' },
  { code: 'ba-RU', name: 'Bashkir' },
  { code: 'eu-ES', name: 'Basque' },
  { code: 'be-BY', name: 'Belarusian' },
  { code: 'bn-IN', name: 'Bengali' },
  { code: 'bs-BA', name: 'Bosnian' },
  { code: 'bg-BG', name: 'Bulgarian' },
  { code: 'ca-ES', name: 'Catalan' },
  { code: 'ckb-IR', name: 'Central Kurdish, Iran' },
  { code: 'ckb-IQ', name: 'Central Kurdish, Iraq' },
  { code: 'zh-HK', name: 'Chinese (Cantonese), Hong-Kong' },
  { code: 'zh-CN', name: 'Chinese, Simplified' },
  { code: 'zh-TW', name: 'Chinese, Traditional' },
  { code: 'hr-HR', name: 'Croatian' },
  { code: 'cs-CZ', name: 'Czech' },
  { code: 'da-DK', name: 'Danish' },
  { code: 'nl-NL', name: 'Dutch' },
  { code: 'en-AU', name: 'English, Australian' },
  { code: 'en-GB', name: 'English, British' },
  { code: 'en-IN', name: 'English, Indian' },
  { code: 'en-IE', name: 'English, Irish' },
  { code: 'en-NZ', name: 'English, New Zealand' },
  { code: 'en-AB', name: 'English, Scottish' },
  { code: 'en-ZA', name: 'English, South African' },
  { code: 'en-US', name: 'English, US' },
  { code: 'en-WL', name: 'English, Welsh' },
  { code: 'et-ET', name: 'Estonian' },
  { code: 'fa-IR', name: 'Farsi' },
  { code: 'fi-FI', name: 'Finnish' },
  { code: 'fr-FR', name: 'French' },
  { code: 'fr-CA', name: 'French, Canadian' },
  { code: 'gl-ES', name: 'Galician' },
  { code: 'ka-GE', name: 'Georgian' },
  { code: 'de-DE', name: 'German' },
  { code: 'de-CH', name: 'German, Swiss' },
  { code: 'el-GR', name: 'Greek' },
  { code: 'gu-IN', name: 'Gujarati' },
  { code: 'ha-NG', name: 'Hausa' },
  { code: 'he-IL', name: 'Hebrew' },
  { code: 'hi-IN', name: 'Hindi, Indian' },
  { code: 'hu-HU', name: 'Hungarian' },
  { code: 'is-IS', name: 'Icelandic' },
  { code: 'id-ID', name: 'Indonesian' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'kab-DZ', name: 'Kabyle' },
  { code: 'kn-IN', name: 'Kannada' },
  { code: 'kk-KZ', name: 'Kazakh' },
  { code: 'rw-RW', name: 'Kinyarwanda' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'ky-KG', name: 'Kyrgyz' },
  { code: 'lv-LV', name: 'Latvian' },
  { code: 'lt-LT', name: 'Lithuanian' },
  { code: 'lg-IN', name: 'Luganda' },
  { code: 'mk-MK', name: 'Macedonian' },
  { code: 'ms-MY', name: 'Malay' },
  { code: 'ml-IN', name: 'Malayalam' },
  { code: 'mt-MT', name: 'Maltese' },
  { code: 'mr-IN', name: 'Marathi' },
  { code: 'mhr-RU', name: 'Meadow Mari' },
  { code: 'mn-MN', name: 'Mongolian' },
  { code: 'no-NO', name: 'Norwegian Bokmål' },
  { code: 'or-IN', name: 'Odia/Oriya' },
  { code: 'ps-AF', name: 'Pashto' },
  { code: 'pl-PL', name: 'Polish' },
  { code: 'pt-PT', name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese, Brazilian' },
  { code: 'pa-IN', name: 'Punjabi' },
  { code: 'ro-RO', name: 'Romanian' },
  { code: 'ru-RU', name: 'Russian' },
  { code: 'sr-RS', name: 'Serbian' },
  { code: 'si-LK', name: 'Sinhala' },
  { code: 'sk-SK', name: 'Slovak' },
  { code: 'sl-SI', name: 'Slovenian' },
  { code: 'so-SO', name: 'Somali' },
  { code: 'es-ES', name: 'Spanish' },
  { code: 'es-US', name: 'Spanish, US' },
  { code: 'su-ID', name: 'Sundanese' },
  { code: 'sw-KE', name: 'Swahili, Kenya' },
  { code: 'sw-BI', name: 'Swahili, Burundi' },
  { code: 'sw-RW', name: 'Swahili, Rwanda' },
  { code: 'sw-TZ', name: 'Swahili, Tanzania' },
  { code: 'sw-UG', name: 'Swahili, Uganda' },
  { code: 'sv-SE', name: 'Swedish' },
  { code: 'tl-PH', name: 'Tagalog/Filipino' },
  { code: 'ta-IN', name: 'Tamil' },
  { code: 'tt-RU', name: 'Tatar' },
  { code: 'te-IN', name: 'Telugu' },
  { code: 'th-TH', name: 'Thai' },
  { code: 'tr-TR', name: 'Turkish' },
  { code: 'uk-UA', name: 'Ukrainian' },
  { code: 'ug-CN', name: 'Uyghur' },
  { code: 'uz-UZ', name: 'Uzbek' },
  { code: 'vi-VN', name: 'Vietnamese' },
  { code: 'cy-WL', name: 'Welsh' },
  { code: 'wo-SN', name: 'Wolof' },
  { code: 'zu-ZA', name: 'Zulu' },
];
